import { useAsanaMCPWorkspaces } from "@/components/EntitySelector/integrations/AsanaMCPSelector/useAsanaMCPWorkspaces";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIntegrationConnection } from "@/hooks/useIntegrationConnection";
import { AsanaMcpWorkspaceEntity, Entity } from "@cohesive/api-entities";
import { getEntityId, Spinner } from "@cohesive/ui-components";
import { ControlProps, optionIs, rankWith } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { Briefcase, ChevronsUpDown, X } from "lucide-react";
import React, { useMemo, useState } from "react";

interface AsanaWorkspaceControlProps extends ControlProps {
  data: AsanaMcpWorkspaceEntity | string | undefined;
}

const AsanaWorkspaceControlComponent: React.FC<AsanaWorkspaceControlProps> = ({
  data,
  enabled,
  handleChange,
  path,
  label,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const integration = useIntegrationConnection("asana");

  // Extract ID from data (handles both string ID and entity object)
  // Handle both string IDs and entity objects
  const selectedId =
    typeof data === "string"
      ? data
      : data
        ? getEntityId(data as unknown as Entity)
        : null;

  // Create a dummy connection to avoid undefined errors in the hook
  const connectionOrDummy =
    integration ||
    ({
      id: "",
      integration_id: "",
    } as any);

  const { data: workspacesData, isLoading } = useAsanaMCPWorkspaces(
    connectionOrDummy,
    {
      enabled: !!integration,
    },
  );

  const workspaces: AsanaMcpWorkspaceEntity[] = (workspacesData ||
    []) as AsanaMcpWorkspaceEntity[];

  const sortedWorkspaces = useMemo(() => {
    return [...workspaces].sort((a, b) => {
      const aName = a.data?.name || "";
      const bName = b.data?.name || "";
      return aName.localeCompare(bName);
    });
  }, [workspaces]);

  const filterFn = (workspace: AsanaMcpWorkspaceEntity) => {
    const name = workspace.data?.name || "";
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  };

  const renderWorkspaceIcon = () => {
    return (
      <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
        <Briefcase className="w-4! h-4!" />
      </div>
    );
  };

  const renderFn = (workspace: AsanaMcpWorkspaceEntity) => {
    const workspaceId = workspace.data?.id || "";
    const workspaceName = workspace.data?.name || "";

    return (
      <CommandItem
        key={workspaceId}
        value={workspaceId}
        onSelect={() => {
          // Return full entity object
          handleChange(path, workspace);
          setOpen(false);
        }}
      >
        {renderWorkspaceIcon()}
        <span className="truncate">{workspaceName}</span>
      </CommandItem>
    );
  };

  const selectedWorkspace = useMemo(
    () => workspaces.find((workspace) => selectedId === workspace.data?.id),
    [workspaces, selectedId],
  );

  const isDisabled = !enabled || !integration;

  return (
    <div>
      {label && <label className="text-sm font-medium">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            disabled={isDisabled}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedWorkspace ? (
              <div className="flex items-center gap-2 overflow-hidden">
                <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
                  <Briefcase className="w-4! h-4!" />
                </div>
                <span className="truncate">{selectedWorkspace.data?.name}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">
                {integration ? "Select workspace..." : "Connect Asana first..."}
              </span>
            )}
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {selectedWorkspace && (
                <div
                  role="button"
                  aria-label="Clear selection"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleChange(path, null);
                  }}
                >
                  <X className="h-4 w-4 opacity-50 hover:opacity-100" />
                </div>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search workspaces..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              <CommandEmpty>
                {isLoading ? (
                  <Spinner size="md" />
                ) : integration ? (
                  "No workspaces found."
                ) : (
                  "Connect Asana first."
                )}
              </CommandEmpty>
              <CommandGroup>
                {sortedWorkspaces.filter(filterFn).map(renderFn)}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const asanaWorkspaceTester = rankWith(
  5,
  optionIs("control", "asana_workspace"),
);
export const AsanaWorkspaceControl = withJsonFormsControlProps(
  AsanaWorkspaceControlComponent,
);
